import { getAdobeAccessToken } from '../auth/adobeIms';
import { actionUrl } from './database';
import { AI_SYSTEM_CONTEXT } from './aiContext';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface SseEvent {
  event: string;
  data: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = getAdobeAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getPageContext() {
  return { path: '/home/workspaces', object_details: null };
}

/** The ai-agent page context scoped to a specific Workfront issue. */
export function issuePageContext(requestId: string) {
  return {
    path: `/issue/${requestId}/updates`,
    object_details: { code: 'OPTASK', display_name: 'Issue', id: requestId },
  };
}

/**
 * Call the AI agent proxy action. Returns parsed SSE events as an array.
 */
async function callAiProxy(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<SseEvent[]> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('ai-agent-wf'), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      path,
      body,
      aiToken: token,
    }),
    signal,
  });

  if (!resp.ok) throw new Error(`AI proxy failed: HTTP ${resp.status} | check credentials, ensure "Workfront AdobeRM" org is selected.`);

  const json = await resp.json();
  const data = json.body || json;
  return Array.isArray(data.events) ? data.events : [];
}

/**
 * Initiate a new conversation. Returns parsed SSE events and the conversation_id.
 */
export async function initiateConversation(
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
): Promise<string | null> {
  const events = await callAiProxy(
    'conversations?channel=stream&routing=router',
    {
      page_context: getPageContext(),
      query: AI_SYSTEM_CONTEXT,
      application: 'workfront',
    },
    signal,
  );

  let conversationId: string | null = null;

  for (const evt of events) {
    if (evt.event === 'init') {
      try {
        const parsed = JSON.parse(evt.data);
        conversationId = parsed.conversation_id || null;
      } catch { /* ignore */ }
    }
    onEvent(evt);
  }

  return conversationId;
}

export interface SendMessageOptions {
  /** Prepend the AI_SYSTEM_CONTEXT terminology preamble. Defaults to true. */
  includeContext?: boolean;
  /** ai-agent page context. Defaults to the generic workspace context. */
  pageContext?: { path: string; object_details: { code: string; display_name: string; id: string } | null };
}

/**
 * Send a message in an existing conversation. Returns parsed SSE events.
 */
export async function sendMessage(
  conversationId: string,
  query: string,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
  options?: SendMessageOptions,
): Promise<void> {
  const includeContext = options?.includeContext ?? true;
  const fullQuery = includeContext ? `${AI_SYSTEM_CONTEXT}\n\nUser query: ${query}` : query;
  const events = await callAiProxy(
    `conversations/${conversationId}?channel=stream&routing=router`,
    {
      page_context: options?.pageContext ?? getPageContext(),
      query: fullQuery,
      application: 'workfront',
    },
    signal,
  );

  for (const evt of events) {
    onEvent(evt);
  }
}

// ─── localStorage persistence ─────────────────────────────────────────────

const STORAGE_KEY = 'ai-chat-history';

export function loadChatHistory(): { conversationId: string | null; messages: ChatMessage[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { conversationId: null, messages: [] };
    const parsed = JSON.parse(raw);
    return {
      conversationId: parsed.conversationId || null,
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
    };
  } catch {
    return { conversationId: null, messages: [] };
  }
}

export function saveChatHistory(conversationId: string | null, messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ conversationId, messages }));
  } catch { /* quota exceeded — ignore */ }
}

export function clearChatHistory(): void {
  localStorage.removeItem(STORAGE_KEY);
}
